// ============================================================================
// Encadrer une racine carrée (moteur v13) : la feuille des encadrements.
// ============================================================================
// « √40 est compris entre… » : on cherche les deux carrés qui encadrent 40.
//   6² = 36            (un carré : le calcul doit être juste)
//   7² = 49
//   36 < 40 < 49       (encadrer le nombre sous la racine par les deux carrés)
//   6 < √40 < 7        (prendre les racines : c'est la réponse)
// Les comparaisons en chaîne (« a < b < c ») sont jugées vraies ou fausses en
// valeurs. La conclusion n'est un pas que si l'encadrement par les carrés est
// écrit (en une ligne, ou en deux moitiés « 36 < 40 » et « 40 < 49 »).
// Les bornes peuvent être des décimaux (au dixième : 3,1 < √10 < 3,2) ou
// quelconques (ranger : 5,2 < √30 < 6). Mode séparé : rien ne change ailleurs.
// ============================================================================

import { type Chip, lex, normalize, normalizeChips } from './lexer';
import { close, ev, type Line, type Node, parseExpr, parseLine, lineText } from './tree';

export interface BoundsSpec {
  /** l'expression encadrée, écrite comme dans la feuille : « √40 » */
  expr: string;
  /**
   * les bornes attendues : « 6 » et « 7 », « 3,1 » et « 3,2 ». Une seule borne
   * (moteur v14) : comparer à un nombre (√17 < 4,2) ou à une expression (2√3 < 3√2).
   */
  lower?: string;
  upper?: string;
  /** corrigé, rejoué par le juge à la validation */
  path: string[];
}

interface BoundsState {
  spec: BoundsSpec;
  /** valeur de l'expression (√40) et de ce qui est sous la racine (40) */
  value: number;
  inner: number;
  lo: number | null;
  hi: number | null;
  lowOk: boolean;
  highOk: boolean;
  done: boolean;
  written: Set<string>;
}

export interface BoundsVerdict {
  kind:
    | 'empty'
    | 'open'
    | 'syntax'
    | 'trap'
    | 'false'
    | 'useless'
    | 'unjustified'
    | 'step'
    | 'pending';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  final?: boolean;
  hora?: number;
  trap?: { line: Line; title: string; why: string };
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  /** ce que la ligne établit (lu par addBoundsLine) */
  bnd?: { low: boolean; high: boolean; done?: boolean };
  /**
   * noyau (moteur v34) : la ligne est JUSTE mais ne se justifie pas encore (la conclusion
   * sans la comparaison des carrés). L'ancien moteur la refuse ; le noyau la garde, sans
   * HORA (D7).
   */
  keep?: { line: Line; kind: 'jump' | 'aside' };
}

const REL = new Set(['<', '≤', '>', '≥']);
const num = (t: string): number => ev(parseExpr(normalize(lex(t))), {});

function innerOf(expr: Node): number {
  return expr.k === 'sqrt' ? ev(expr.a!, {}) : ev(expr, {}) ** 2;
}

export function createBoundsState(spec: BoundsSpec): BoundsState {
  const e = parseExpr(normalize(lex(spec.expr)));
  return {
    spec,
    value: ev(e, {}),
    inner: innerOf(e),
    lo: spec.lower === undefined ? null : num(spec.lower),
    hi: spec.upper === undefined ? null : num(spec.upper),
    lowOk: false,
    highOk: false,
    done: false,
    written: new Set(),
  };
}

type Parsed =
  | { k: 'eq'; line: Line; sides: Node[] }
  | { k: 'cmp'; line: Line; terms: Node[]; strict: boolean[] };

/** lit une ligne : un calcul (« 6² = 36 ») ou une comparaison en chaîne */
function parse(atoms: string[]): Parsed {
  const rels = atoms.filter((a) => REL.has(a));
  if (!rels.length) {
    const line = parseLine(atoms);
    if (line.dis.length !== 1 || line.dis[0]!.cmp)
      throw new Error('Écris un calcul ou une comparaison.');
    return { k: 'eq', line, sides: line.dis[0]!.sides };
  }
  if (atoms.includes('=')) throw new Error('Pas de « = » dans une comparaison.');
  const down = rels.every((r) => r === '>' || r === '≥');
  const up = rels.every((r) => r === '<' || r === '≤');
  if (!down && !up)
    throw new Error('Une comparaison va dans un seul sens : tout en « < », ou tout en « > ».');
  const segs: string[][] = [[]];
  atoms.forEach((a) => (REL.has(a) ? segs.push([]) : segs[segs.length - 1]!.push(a)));
  if (segs.some((s) => !s.length)) throw new Error('Il manque un nombre.');
  let terms = segs.map((s) => parseExpr(s));
  let strict = rels.map((r) => r === '<' || r === '>');
  // « 7 > √40 > 6 » se lit « 6 < √40 < 7 »
  if (down) {
    terms = terms.reverse();
    strict = strict.reverse();
  }
  const line: Line = { dis: [{ sides: terms, cmp: '<' }] };
  return { k: 'cmp', line, terms, strict };
}

function looksOpen(atoms: string[]): boolean {
  const last = atoms[atoms.length - 1];
  // une parenthèse encore ouverte : la ligne n'est pas finie
  const depth = atoms.reduce((d, a) => d + (a === '(' ? 1 : a === ')' ? -1 : 0), 0);
  if (depth > 0) return true;
  return !last || REL.has(last) || ['=', '+', '−', '×', '÷', '(', '√', '^'].includes(last);
}

const isSq = (t: Node, v: number): boolean =>
  ((t.k === 'sq' && close(ev(t.a!, {}), v)) ||
    (t.k === 'pow' && close(ev(t.a!, {}), v) && close(ev(t.b!, {}), 2)) ||
    (t.k === 'mul' && t.c!.length === 2 && t.c!.every((c) => close(ev(c, {}), v)))) &&
  true;

const step = (
  line: Line,
  atoms: string[],
  label: string,
  hora: number,
  why: string,
  final = false,
): BoundsVerdict => ({
  kind: 'step',
  atoms,
  line,
  final,
  hora,
  rule: { id: 'encadrement', card: 'Encadrer une racine carrée', label, hora, why },
});

export function boundsJudge(
  bs: BoundsState,
  traps: { line: string; title: string; why: string }[],
  chips: Chip[],
  opts?: { verify?: boolean },
): BoundsVerdict {
  // un bout peut porter une ligne entière (corrigé rejoué) : on la relit
  const A = normalize(normalizeChips(chips).atoms.flatMap((a) => (/\s/.test(a) ? lex(a) : [a])));
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  let p: Parsed;
  try {
    p = parse(A);
  } catch (e) {
    if (looksOpen(A)) return { kind: 'open', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: (e as Error).message,
      badChip: chips.length - 1,
    };
  }
  const text = lineText(p.line);
  const trap = traps.find((t) => {
    try {
      return lineText(parse(normalize(lex(t.line))).line) === text;
    } catch {
      return false;
    }
  });
  if (trap)
    return {
      kind: 'trap',
      atoms: A,
      title: 'Piège : ' + trap.title.charAt(0).toLowerCase() + trap.title.slice(1),
      message: trap.why,
      trap: { line: p.line, title: trap.title, why: trap.why },
      badChip: -1,
    };
  if (bs.written.has(text))
    return {
      kind: 'useless',
      atoms: A,
      line: p.line,
      title: 'Tu l’as déjà écrit',
      message: 'Cette ligne est déjà dans ta feuille.',
    };
  let vals: number[];
  try {
    vals = (p.k === 'eq' ? p.sides : p.terms).map((t) => ev(t, {}));
  } catch {
    vals = [NaN];
  }
  if (vals.some((v) => !isFinite(v)))
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Un nombre seulement',
      message: 'Ici, on ne calcule qu’avec des nombres.',
      badChip: -1,
    };
  if (p.k === 'eq') {
    if (!vals.every((v) => close(v, vals[0]!)))
      return {
        kind: 'false',
        atoms: A,
        line: p.line,
        title: 'Calcul faux',
        message: 'Vérifie ce calcul.',
      };
    const sq = [bs.lo, bs.hi, bs.value].find(
      (b) => b !== null && close(vals[0]!, b * b) && p.sides.some((s) => isSq(s, b)),
    );
    if (sq === undefined)
      return {
        kind: 'useless',
        atoms: A,
        line: p.line,
        title: 'Juste, mais à côté',
        message: 'Calcule les carrés des nombres à comparer.',
      };
    return step(
      p.line,
      A,
      'Carré',
      1,
      'On calcule le carré pour le comparer au nombre sous la racine.',
    );
  }
  // comparaison en chaîne : vraie ?
  for (let i = 0; i + 1 < vals.length; i++) {
    const ok = p.strict[i]
      ? vals[i]! < vals[i + 1]! && !close(vals[i]!, vals[i + 1]!)
      : vals[i]! <= vals[i + 1]! + 1e-9;
    if (!ok)
      return {
        kind: 'false',
        atoms: A,
        line: p.line,
        title: 'Comparaison fausse',
        message:
          'Compare les valeurs : ' +
          vals.map((v) => String(+v.toFixed(4)).replace('.', ',')).join(' ; ') +
          '.',
      };
  }
  const isRoot = (t: Node) => close(ev(t, {}), bs.value) && JSON.stringify(t).includes('sqrt');
  const isInner = (v: number) => close(v, bs.inner);
  const lo2 = bs.lo === null ? NaN : bs.lo * bs.lo;
  const hi2 = bs.hi === null ? NaN : bs.hi * bs.hi;
  const both = bs.lo !== null && bs.hi !== null;
  // l'encadrement par les carrés : 36 < 40 < 49, ou une moitié
  if (!p.terms.some(isRoot)) {
    let low = false;
    let high = false;
    if (vals.length === 3 && isInner(vals[1]!)) {
      low = close(vals[0]!, lo2);
      high = close(vals[2]!, hi2);
    } else if (vals.length === 2) {
      low = close(vals[0]!, lo2) && isInner(vals[1]!);
      high = isInner(vals[0]!) && close(vals[1]!, hi2);
    }
    if (!low && !high)
      return {
        kind: 'useless',
        atoms: A,
        line: p.line,
        title: 'Juste, mais à côté',
        message:
          'Encadre ' +
          String(bs.inner).replace('.', ',') +
          ' par les carrés des deux nombres cherchés.',
      };
    const full = (bs.lo === null || low) && (bs.hi === null || high);
    return {
      ...step(
        p.line,
        A,
        full ? (both ? 'Encadrer par des carrés' : 'Comparer les carrés') : 'Comparer à un carré',
        full ? 2 : 1,
        both
          ? 'Le nombre sous la racine est entre deux carrés.'
          : 'On compare les carrés : ils sont rangés comme les nombres positifs.',
      ),
      bnd: { low, high },
    };
  }
  // la conclusion : lo < √a < hi (ou une seule borne)
  const at = bs.lo === null ? 0 : 1;
  if (vals.length !== (both ? 3 : 2) || !isRoot(p.terms[at]!))
    return {
      kind: 'useless',
      atoms: A,
      line: p.line,
      title: both ? 'L’encadrement complet' : 'La comparaison demandée',
      message: 'Écris : ' + boundsGoalText(bs.spec) + '.',
    };
  if (
    (bs.lo !== null && !close(vals[0]!, bs.lo)) ||
    (bs.hi !== null && !close(vals[vals.length - 1]!, bs.hi))
  )
    return {
      kind: 'useless',
      atoms: A,
      line: p.line,
      title: 'Juste, mais pas l’encadrement demandé',
      message: 'Cherche des bornes plus proches.',
    };
  if (!((bs.lo === null || bs.lowOk) && (bs.hi === null || bs.highOk))) {
    if (!opts?.verify) return { kind: 'pending', atoms: A, complete: true };
    return {
      kind: 'unjustified',
      atoms: A,
      line: p.line,
      keep: { line: p.line, kind: 'jump' },
      title: 'Justifie avec les carrés',
      message:
        'Écris d’abord la comparaison des carrés (' +
        [bs.lo === null ? null : lo2, bs.inner, bs.hi === null ? null : hi2]
          .filter((v): v is number => v !== null)
          .map((v) => String(+v.toFixed(6)).replace('.', ','))
          .join(' < ') +
        ').',
    };
  }
  return {
    ...step(
      p.line,
      A,
      both ? 'Prendre les racines' : 'Conclure',
      2,
      'La racine carrée conserve l’ordre des nombres positifs.',
      true,
    ),
    bnd: { low: false, high: false, done: true },
  };
}

export function addBoundsLine(bs: BoundsState, v: BoundsVerdict): void {
  if (!v.line) return;
  bs.written.add(lineText(v.line));
  // noyau (moteur v34) : la conclusion gardée sans HORA (D7) atteint le but
  if (v.kind === 'useless' && v.bnd?.done) bs.done = true;
  if (v.kind !== 'step') return;
  if (!v.bnd) return;
  if (v.bnd.done) bs.done = true;
  if (v.bnd.low) bs.lowOk = true;
  if (v.bnd.high) bs.highOk = true;
}
export function boundsGoalReached(bs: BoundsState): boolean {
  return bs.done;
}
export function boundsEstablished(bs: BoundsState, L: Line): boolean {
  return bs.written.has(lineText(L));
}
export function boundsGoalText(spec: BoundsSpec): string {
  return [spec.lower, spec.expr, spec.upper].filter((t) => t !== undefined).join(' < ');
}
/** le texte (choix du QCM) est-il l'encadrement demandé ? */
export function boundsIsGoal(bs: BoundsState, text: string): boolean {
  try {
    return (
      lineText(parse(normalize(lex(text))).line) ===
      lineText(parse(normalize(lex(boundsGoalText(bs.spec)))).line)
    );
  } catch {
    return false;
  }
}
export function boundsPathLines(spec: BoundsSpec): Line[] {
  return spec.path.map((t) => parse(normalize(lex(t))).line);
}
